/**
 * PopularTickers - Grid of popular stocks using Simulacra design system
 */
import React from 'react';
import { YStack, XStack, H2, Text } from 'tamagui';
import { GlassCard } from './base';

interface Ticker {
  symbol: string;
  name: string;
  description?: string;
  trend?: 'up' | 'down' | 'neutral';
}

interface PopularTickersProps {
  onSelectTicker: (ticker: string) => void;
}

const PopularTickers: React.FC<PopularTickersProps> = ({ onSelectTicker }) => {
  // Curated list of popular/interesting tickers
  const popularTickers: Ticker[] = [
    { symbol: 'TSLA', name: 'Tesla', description: 'Electric vehicles & energy', trend: 'up' },
    { symbol: 'AAPL', name: 'Apple', description: 'Consumer electronics & services', trend: 'up' },
    { symbol: 'NVDA', name: 'NVIDIA', description: 'AI chips & computing', trend: 'up' },
    { symbol: 'MSTR', name: 'MicroStrategy', description: 'Business intelligence & Bitcoin', trend: 'up' },
    { symbol: 'GOOGL', name: 'Google', description: 'Search & cloud services', trend: 'neutral' },
    { symbol: 'META', name: 'Meta', description: 'Social media & VR', trend: 'up' },
    { symbol: 'MSFT', name: 'Microsoft', description: 'Software & cloud services', trend: 'up' },
    { symbol: 'AMZN', name: 'Amazon', description: 'E-commerce & cloud', trend: 'neutral' },
    { symbol: 'COIN', name: 'Coinbase', description: 'Cryptocurrency exchange', trend: 'up' },
    { symbol: 'PLTR', name: 'Palantir', description: 'Data analytics & AI', trend: 'up' },
    { symbol: 'IREN', name: 'Iris Energy', description: 'Bitcoin mining & energy', trend: 'up' },
    { symbol: 'AMD', name: 'AMD', description: 'Processors & graphics', trend: 'neutral' }
  ];

  const getTrendChange = (trend?: 'up' | 'down' | 'neutral') => {
    // Return mock percentage based on trend
    switch (trend) {
      case 'up': return '+2.5%';
      case 'down': return '-1.8%';
      default: return '+0.3%';
    }
  };

  return (
    <YStack gap="$5" width="100%">
      {/* Header */}
      <YStack gap="$2" alignItems="center">
        <H2 color="$green10" fontFamily="$heading" textAlign="center">
          Popular Stocks
        </H2>
        <Text color="$colorPress" fontFamily="$body" fontSize="$3" textAlign="center">
          Click any ticker to view its latest earnings summary
        </Text>
      </YStack>

      {/* Ticker Grid */}
      <XStack flexWrap="wrap" gap="$3" justifyContent="center">
        {popularTickers.map((ticker) => (
          <GlassCard
            key={ticker.symbol}
            glowOnHover
            onPress={() => onSelectTicker(ticker.symbol)}
            pressStyle={{ scale: 0.98 }}
            cursor="pointer"
            minWidth={240}
            maxWidth={280}
            flex={1}
            $sm={{ minWidth: 200, maxWidth: '100%' }}
          >
            <YStack gap="$2">
              {/* Symbol and change */}
              <XStack justifyContent="space-between" alignItems="center">
                <Text
                  fontFamily="$heading"
                  fontSize="$5"
                  fontWeight="bold"
                  color="$green10"
                >
                  {ticker.symbol}
                </Text>
                <Text
                  fontFamily="$mono"
                  fontSize="$3"
                  color={ticker.trend === 'up' ? '$cyan10' : ticker.trend === 'down' ? '$red10' : '$placeholderColor'}
                  fontWeight="600"
                >
                  {getTrendChange(ticker.trend)}
                </Text>
              </XStack>

              {/* Company name */}
              <Text
                fontFamily="$body"
                fontSize="$4"
                color="$color"
                fontWeight="500"
              >
                {ticker.name}
              </Text>

              {/* Description */}
              {ticker.description && (
                <Text
                  fontFamily="$body"
                  fontSize="$2"
                  color="$placeholderColor"
                  numberOfLines={2}
                >
                  {ticker.description}
                </Text>
              )}
            </YStack>
          </GlassCard>
        ))}
      </XStack>

      {/* Footer tip */}
      <GlassCard backgroundColor="$backgroundHover" borderColor="$borderPastel">
        <XStack gap="$2" alignItems="center" flexWrap="wrap">
          <Text fontSize="$5">💡</Text>
          <Text color="$colorPress" fontFamily="$body" fontSize="$2" flex={1}>
            Tip: Use the search box above to find any stock ticker, or browse our AI-powered earnings summaries
          </Text>
        </XStack>
      </GlassCard>
    </YStack>
  );
};

export default PopularTickers;
